import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { getPostings } from '@/services/staffing/Staffing';
import { Posting } from '@/types/staffing/posting';
import { ODataParams } from '@/utils/odataUtil';
import { useApiClient } from './ApiContext';

export type StaffingView = 'analyst' | 'assistant_rbt' | 'assessment' | 'service_request';

type StaffingContextType = {
    postings: Record<StaffingView, Posting[]>;
    totals: Record<StaffingView, number>;
    loading: Record<StaffingView, boolean>;
    errors: Record<StaffingView, string | null>;
    fetchPostings: (view: StaffingView, params: ODataParams) => Promise<void>;
    refreshPostings: (view: StaffingView) => Promise<void>;
    currentRegion: string | null;
    setCurrentRegion: (region: string | null) => void;
}

const initialPostings: Record<StaffingView, Posting[]> = {
    analyst: [],
    assistant_rbt: [],
    assessment: [],
    service_request: []
};

const initialTotals: Record<StaffingView, number> = {
    analyst: 0,
    assistant_rbt: 0,
    assessment: 0,
    service_request: 0
};

const initialLoading: Record<StaffingView, boolean> = {
    analyst: false,
    assistant_rbt: false,
    assessment: false,
    service_request: false
};

const initialErrors: Record<StaffingView, string | null> = {
    analyst: null,
    assistant_rbt: null,
    assessment: null,
    service_request: null
};

const StaffingContext = createContext<StaffingContextType>({
    postings: initialPostings,
    totals: initialTotals,
    loading: initialLoading,
    errors: initialErrors,
    fetchPostings: () => Promise.resolve(),
    refreshPostings: () => Promise.resolve(),
    currentRegion: null,
    setCurrentRegion: () => {}
});

const initialLastParams: Record<StaffingView, ODataParams | null> = {
    analyst: null,
    assistant_rbt: null,
    assessment: null,
    service_request: null
};

export const StaffingProvider = ({ children }: { children: ReactNode }) => {
    const api = useApiClient();
    const [postings, setPostings] = useState<Record<StaffingView, Posting[]>>(initialPostings);
    const [totals, setTotals] = useState<Record<StaffingView, number>>(initialTotals);
    const [loading, setLoading] = useState<Record<StaffingView, boolean>>(initialLoading);
    const [errors, setErrors] = useState<Record<StaffingView, string | null>>(initialErrors);
    const [currentRegion, setCurrentRegion] = useState<string | null>(null);
    const [lastParams, setLastParams] = useState<Record<StaffingView, ODataParams | null>>(initialLastParams);
    const requestSeq = useRef<Record<StaffingView, number>>({ analyst: 0, assistant_rbt: 0, assessment: 0, service_request: 0 });

    const fetchPostings = useCallback((view: StaffingView, params: ODataParams): Promise<void> => {
        const seq = ++requestSeq.current[view];
        setLastParams(prev => ({ ...prev, [view]: params }));
        setLoading(prev => ({ ...prev, [view]: true }));
        return getPostings({ ...params, count: true }, api)
            .then(res => {
                if (seq !== requestSeq.current[view]) return;
                const isEnvelope = res.data?.value !== undefined;
                const raw = isEnvelope ? res.data.value : res.data;
                const count = res.data?.['@odata.count'] ?? (Array.isArray(raw) ? raw.length : 0);
                const items = raw.map((p: Posting & { Reasons: string | null }) => ({
                    ...p,
                    Reasons: p.Reasons ? JSON.parse(p.Reasons) : null
                }));
                setPostings(prev => ({ ...prev, [view]: items }));
                setTotals(prev => ({ ...prev, [view]: count }));
                setErrors(prev => ({ ...prev, [view]: null }));
            })
            .catch(err => {
                console.error(`Error fetching postings for view "${view}":`, err);
                if (seq === requestSeq.current[view]) {
                    setErrors(prev => ({ ...prev, [view]: 'Failed to load postings. Please try again.' }));
                }
            })
            .finally(() => {
                if (seq === requestSeq.current[view]) {
                    setLoading(prev => ({ ...prev, [view]: false }));
                }
            });
    }, [api]);

    const refreshPostings = useCallback((view: StaffingView): Promise<void> => {
        const params = lastParams[view];
        if (!params) return Promise.resolve();
        return fetchPostings(view, params);
    }, [lastParams, fetchPostings]);

    return (
        <StaffingContext.Provider value={{ postings, totals, loading, errors, fetchPostings, refreshPostings, currentRegion, setCurrentRegion }}>
            {children}
        </StaffingContext.Provider>
    );
};

export const useStaffing = () => useContext(StaffingContext);
